import React, { useCallback } from 'react'
import { Box, Button, Menu, MenuItem } from '@mui/material'
import { Appid, Operatingunit } from '@cbgms/api'
import { changeDachUserRole, changeUserOperatingUnit, changeUserRole } from '../auth/state/actions'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/reducers'
import { Roles } from '@cbgms/api/modules/enums'
import { ROLES_OPTIONS } from 'core/constants/roles'
import { OperatingunitRoles, OperatingunitRolesOptionNames } from '@carsys/enums/admin-ui-operation-units'

const getAppIdForOperatingUnit = (value: Operatingunit) => {
  let appid
  if (value === Operatingunit.ModelOu) {
    appid = Appid.ModelOuAppId
  } else if (value === Operatingunit.PVOu) {
    appid = Appid.PVOuAppId
  } else {
    appid = Appid.StakisOuAppId
  }
  return appid
}

function roleToOperatingUnitOptions(roles: string[] | undefined) {
  const options = roles?.map(role => {
    const normalizedRole = role.toLowerCase()
    let operationUnitName
    let roleText
    let appid
    switch (normalizedRole) {
      case OperatingunitRoles.dach_support_admin_pva.toLowerCase():
        operationUnitName = Operatingunit.PVOu
        roleText = OperatingunitRolesOptionNames.dach_support_admin_pva
        appid = getAppIdForOperatingUnit(Operatingunit.PVOu)
        break
      case OperatingunitRoles.dach_telesales_pva.toLowerCase():
        operationUnitName = Operatingunit.PVOu
        roleText = OperatingunitRolesOptionNames.dach_telesales_pva
        appid = getAppIdForOperatingUnit(Operatingunit.PVOu)
        break
      case OperatingunitRoles.dach_telesales_supervisor_pva.toLowerCase():
        operationUnitName = Operatingunit.PVOu
        roleText = OperatingunitRolesOptionNames.dach_telesales_supervisor_pva
        appid = getAppIdForOperatingUnit(Operatingunit.PVOu)
        break
      case OperatingunitRoles.dach_support_desk_pva.toLowerCase():
        operationUnitName = Operatingunit.PVOu
        roleText = OperatingunitRolesOptionNames.dach_support_desk_pva
        appid = getAppIdForOperatingUnit(Operatingunit.PVOu)
        break
      case OperatingunitRoles.dach_super_user_pva.toLowerCase():
        operationUnitName = Operatingunit.PVOu
        roleText = OperatingunitRolesOptionNames.dach_super_user_pva
        appid = getAppIdForOperatingUnit(Operatingunit.PVOu)
        break
      case OperatingunitRoles.dach_support_admin_sg.toLowerCase():
        operationUnitName = Operatingunit.StakisOu
        roleText = OperatingunitRolesOptionNames.dach_support_admin_sg
        appid = getAppIdForOperatingUnit(Operatingunit.StakisOu)
        break
      case OperatingunitRoles.dach_telesales_sg.toLowerCase():
        operationUnitName = Operatingunit.StakisOu
        roleText = OperatingunitRolesOptionNames.dach_telesales_sg
        appid = getAppIdForOperatingUnit(Operatingunit.StakisOu)
        break
      case OperatingunitRoles.dach_telesales_supervisor_sg.toLowerCase():
        operationUnitName = Operatingunit.StakisOu
        roleText = OperatingunitRolesOptionNames.dach_telesales_supervisor_sg
        appid = getAppIdForOperatingUnit(Operatingunit.StakisOu)
        break
      case OperatingunitRoles.dach_support_desk_sg.toLowerCase():
        operationUnitName = Operatingunit.StakisOu
        roleText = OperatingunitRolesOptionNames.dach_support_desk_sg
        appid = getAppIdForOperatingUnit(Operatingunit.StakisOu)
        break
      case OperatingunitRoles.dach_super_user_sg.toLowerCase():
        operationUnitName = Operatingunit.StakisOu
        roleText = OperatingunitRolesOptionNames.dach_super_user_sg
        appid = getAppIdForOperatingUnit(Operatingunit.StakisOu)
        break
      case OperatingunitRoles.dach_marketing_user_pva.toLowerCase():
        operationUnitName = Operatingunit.PVOu
        roleText = OperatingunitRolesOptionNames.dach_marketing_user_pva
        appid = getAppIdForOperatingUnit(Operatingunit.PVOu)
        break
      case OperatingunitRoles.dach_marketing_user_sg.toLowerCase():
        operationUnitName = Operatingunit.StakisOu
        roleText = OperatingunitRolesOptionNames.dach_marketing_user_sg
        appid = getAppIdForOperatingUnit(Operatingunit.StakisOu)
        break
      default:
        roleText = ROLES_OPTIONS.find(option => option.key === normalizedRole)?.text || ''
    }
    return { key: operationUnitName, value: operationUnitName, text: roleText, appid, role: normalizedRole }
  })

  return options
}

enum GermanyRegionUrl {
  Production = 'de.carsys.online',
  Develop = 'develop.de.carsys.online',
  Staging = 'staging.de.carsys.online',
  RC = 'rc.de.carsys.online',
  Localhost = 'localhost'
}

const GERMANY_REGIONS: string[] = [GermanyRegionUrl.Production, GermanyRegionUrl.Develop, GermanyRegionUrl.Staging, GermanyRegionUrl.RC]

export const useHasGermanyRegionPermission = () => {
  const selectedRegion = useSelector((state: IRootReducerState) => state.region.selectedRegion)
  const [isDERegion, setIsDERegion] = React.useState(false)
  React.useEffect(() => {
    const region = selectedRegion.toLowerCase()
    const isGermany = GERMANY_REGIONS.includes(region)
    setIsDERegion(isGermany)
  }, [selectedRegion])

  const hasPermission = isDERegion || selectedRegion === GermanyRegionUrl.Localhost
  return hasPermission
}

const OperationUnitSelector: React.FC<{}> = () => {
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const roles = useSelector((state: IRootReducerState) => state.auth.user.roles)
  const dachRole = useSelector((state: IRootReducerState) => state.auth.user.dach_role)
  const hasGermanyRegionPermission = useHasGermanyRegionPermission()
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), [setAnchorEl])
  const handleClose = useCallback(() => setAnchorEl(null), [setAnchorEl])
  const DACHUsersRoles = roles?.filter(role => role.toLowerCase().startsWith('dach_'))
  const nonDACHRoles = roles?.filter(role => !role.toLowerCase().startsWith('dach_'))
  const [selectedRole, setSelectedRole] = React.useState<string>(DACHUsersRoles && DACHUsersRoles?.length > 0 ? DACHUsersRoles[0] : '')
  const roleOptions = React.useMemo(() => roleToOperatingUnitOptions(roles), [roles, hasGermanyRegionPermission])
  const initRoleFromOptions = useCallback(() => {
    const role = DACHUsersRoles && DACHUsersRoles?.length > 0 ? DACHUsersRoles[0] : ''
    if (role && roleOptions && roleOptions.length > 0 && dachRole) {
      const matchedOption = roleOptions.find(option => option.role === dachRole)
      if (matchedOption) {
        changeRole(matchedOption)
      }
    }
  }, [dispatch, roles, selectedRole, hasGermanyRegionPermission, roleOptions, dachRole])

  React.useEffect(() => {
    initRoleFromOptions()
  }, [initRoleFromOptions, hasGermanyRegionPermission])

  const changeRole = useCallback(
    ({ value: operatingUnitValue, appid: appId, text: OperatingunitRolesOptionName, role }) => {
      dispatch(changeUserOperatingUnit(operatingUnitValue, appId))
      dispatch(changeDachUserRole(role))
      const roleWithoutUnit = role.toLowerCase().split('-')[0]
      const foundRoleOption = ROLES_OPTIONS.find(role => role.key === roleWithoutUnit)
      if (foundRoleOption && foundRoleOption.text) {
        dispatch(changeUserRole(roleWithoutUnit as Roles))
      }
      setSelectedRole(`${OperatingunitRolesOptionName}`)
      handleClose()
    },
    [dispatch, handleClose]
  )

  React.useEffect(() => {
    if (!dachRole || !hasGermanyRegionPermission) {
      if (nonDACHRoles && nonDACHRoles.length > 0) {
        dispatch(changeUserRole(nonDACHRoles[0] as Roles))
      }
    }
  }, [hasGermanyRegionPermission, dispatch])

  if (!dachRole || !hasGermanyRegionPermission) {
    return null
  }

  return (
    <>
      <Button sx={{ height: '100%' }} color='secondary' data-testid='header-dropdown-ounames' onClick={handleClick}>
        <Box sx={{ paddingLeft: 1 }}>{selectedRole}</Box>
      </Button>
      <Menu open={open} onClose={handleClose} anchorEl={anchorEl} data-testid='header-dropdown-ounames-menu'>
        {roleOptions?.map((option, index) => {
          const optionText = `${option.text}`
          return (
            <MenuItem key={index} onClick={() => changeRole(option)} data-testid={`header-dropdown-ounames-option-${optionText}`}>
              <Box sx={{ paddingLeft: 1 }}>{optionText}</Box>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default OperationUnitSelector
